/* SalesAnalytics — manager-focused analytics page for the Sales Portal.
 *
 * iter98.53 — Phase 2 of the Sales Portal. Renders three blocks:
 *   1. Funnel conversion %: stage-by-stage drop-off table + visual
 *      step-down chart. Headline metric = overall prospect → active %.
 *   2. Leaderboard with drill-down: clicking a rep expands their per-
 *      stage breakdown inline so the manager can see where they're
 *      bottlenecked (a rep with 12 prospects stuck in Discovery looks
 *      VERY different from a rep with 12 in Contract).
 *   3. Territory grid: revenue / cases / active / conversion stacked
 *      bar + numeric grid by N/S/E/W. The chosen MVP visualization
 *      (option 1b — no SVG map).
 *
 * Data source: GET /api/sales/analytics
 */
import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import {
    Funnel, TrendUp, Users, CurrencyInr, Stack, MapPin, CaretRight, CaretDown,
} from "@phosphor-icons/react";


const STAGE_META = {
    prospect:    { label: "Prospect",    color: "#94A3B8" },
    discovery:   { label: "Discovery",   color: "#0EA5E9" },
    demo:        { label: "Lab Demo",    color: "#6366F1" },
    trial:       { label: "Case Trial",  color: "#F59E0B" },
    negotiation: { label: "Negotiation", color: "#EC4899" },
    contract:    { label: "Contract",    color: "#10B981" },
    active:      { label: "Active",      color: "#059669" },
};
const STAGE_ORDER = ["prospect", "discovery", "demo", "trial", "negotiation", "contract", "active"];

const TERRITORY_META = {
    north:   { label: "North",   color: "#0EA5E9" },
    south:   { label: "South",   color: "#10B981" },
    east:    { label: "East",    color: "#F59E0B" },
    west:    { label: "West",    color: "#EC4899" },
    central: { label: "Central", color: "#6366F1" },
    other:   { label: "Other",   color: "#94A3B8" },
};


function fmtINR(n) {
    if (!n) return "₹0";
    if (n >= 10000000) return `₹${(n / 10000000).toFixed(1)}Cr`;
    if (n >= 100000)   return `₹${(n / 100000).toFixed(1)}L`;
    if (n >= 1000)     return `₹${(n / 1000).toFixed(0)}k`;
    return `₹${n}`;
}


export default function SalesAnalytics() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [expandedRep, setExpandedRep] = useState(null);

    useEffect(() => {
        let alive = true;
        api.get("/sales/analytics")
            .then((r) => { if (alive) setData(r.data); })
            .catch(() => null)
            .finally(() => { if (alive) setLoading(false); });
        return () => { alive = false; };
    }, []);

    if (loading) return <div className="p-8 text-sm text-muted-foreground">Loading analytics…</div>;
    if (!data) return <div className="p-8 text-sm text-muted-foreground">No data yet.</div>;

    const { funnel_conversion, overall_conversion_pct, leaderboard, territory_breakdown, total_prospects, is_manager } = data;

    // Max revenue for territory bar scaling
    const maxRev = Math.max(1, ...territory_breakdown.map((t) => t.monthly_revenue));

    return (
        <div className="p-4 sm:p-6 space-y-5 max-w-[1400px] mx-auto" data-testid="sales-analytics">
            <div className="flex items-end justify-between gap-3 flex-wrap">
                <div>
                    <h1 className="font-display text-2xl font-bold tracking-tight">Analytics</h1>
                    <p className="text-xs text-muted-foreground mt-1">
                        {is_manager ? "All territories · all reps" : "Your assigned prospects"} · {total_prospects} prospects total
                    </p>
                </div>
                <div className="bg-white rounded-xl border border-border px-4 py-2.5 flex items-center gap-2">
                    <TrendUp size={18} weight="duotone" className="text-emerald-600" />
                    <div>
                        <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">Prospect → Active</div>
                        <div className="font-display text-xl font-bold tabular-nums text-emerald-600">{overall_conversion_pct}%</div>
                    </div>
                </div>
            </div>

            {/* ── Funnel conversion ── */}
            <section className="bg-white rounded-2xl border border-border p-5" data-testid="analytics-funnel">
                <div className="flex items-center gap-2 mb-4">
                    <Funnel size={16} weight="duotone" className="text-primary" />
                    <h2 className="font-display text-base font-bold">Funnel conversion</h2>
                    <span className="text-[11px] text-muted-foreground">% retained at each stage transition</span>
                </div>
                <div className="grid grid-cols-7 gap-2" data-testid="funnel-steps">
                    {funnel_conversion.map((row, idx) => {
                        const meta = STAGE_META[row.stage];
                        const ConversionIcon = row.conversion_from_prev_pct === null
                            ? null
                            : row.conversion_from_prev_pct >= 60
                            ? "↑" : row.conversion_from_prev_pct >= 30 ? "→" : "↓";
                        const convColor = row.conversion_from_prev_pct === null ? "text-muted-foreground"
                            : row.conversion_from_prev_pct >= 60 ? "text-emerald-600"
                            : row.conversion_from_prev_pct >= 30 ? "text-amber-600" : "text-rose-600";
                        return (
                            <div key={row.stage} className="flex flex-col items-center" data-testid={`funnel-step-${row.stage}`}>
                                <div className="w-2 h-2 rounded-full mb-2" style={{ background: meta.color }} />
                                <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground text-center">
                                    {meta.label}
                                </div>
                                <div className="font-display text-2xl font-bold tabular-nums mt-1">{row.cumulative_count}</div>
                                <div className="text-[9px] text-muted-foreground">cumulative</div>
                                {row.conversion_from_prev_pct !== null && (
                                    <div className={`text-[11px] font-bold mt-2 ${convColor}`} data-testid={`funnel-step-${row.stage}-conv`}>
                                        {ConversionIcon} {row.conversion_from_prev_pct}%
                                    </div>
                                )}
                                {idx === 0 && <div className="text-[9px] text-muted-foreground/70 mt-2">start</div>}
                            </div>
                        );
                    })}
                </div>
                {/* Step-down bars visualisation */}
                <div className="mt-5 space-y-1.5">
                    {funnel_conversion.map((row) => {
                        const meta = STAGE_META[row.stage];
                        const maxCount = funnel_conversion[0].cumulative_count || 1;
                        const widthPct = (row.cumulative_count / maxCount) * 100;
                        return (
                            <div key={row.stage} className="flex items-center gap-2" data-testid={`funnel-bar-${row.stage}`}>
                                <div className="w-20 text-[10px] font-semibold flex items-center gap-1.5 shrink-0">
                                    <span className="w-1.5 h-1.5 rounded-full" style={{ background: meta.color }} />
                                    {meta.label}
                                </div>
                                <div className="flex-1 h-5 bg-secondary/40 rounded overflow-hidden">
                                    <div className="h-full rounded transition-all duration-500" style={{ width: `${widthPct}%`, background: meta.color, opacity: 0.85 }} />
                                </div>
                                <div className="w-12 text-right text-[10px] font-bold tabular-nums">{row.cumulative_count}</div>
                            </div>
                        );
                    })}
                </div>
            </section>

            {/* ── Leaderboard with drill-down ── */}
            <section className="bg-white rounded-2xl border border-border p-5" data-testid="analytics-leaderboard">
                <div className="flex items-center gap-2 mb-3">
                    <Users size={16} weight="duotone" className="text-primary" />
                    <h2 className="font-display text-base font-bold">Rep leaderboard</h2>
                    <span className="text-[11px] text-muted-foreground">Click a row to see their per-stage pipeline</span>
                </div>
                {leaderboard.length === 0 ? (
                    <div className="py-6 text-center text-xs text-muted-foreground">No reps with assigned prospects yet.</div>
                ) : (
                    <div className="space-y-1">
                        {leaderboard.map((r, idx) => (
                            <LeaderboardRow
                                key={r.rep_id}
                                rank={idx + 1}
                                row={r}
                                expanded={expandedRep === r.rep_id}
                                onToggle={() => setExpandedRep(expandedRep === r.rep_id ? null : r.rep_id)}
                            />
                        ))}
                    </div>
                )}
            </section>

            {/* ── Territory breakdown ── */}
            <section className="bg-white rounded-2xl border border-border p-5" data-testid="analytics-territory">
                <div className="flex items-center gap-2 mb-3">
                    <MapPin size={16} weight="duotone" className="text-primary" />
                    <h2 className="font-display text-base font-bold">Territory breakdown</h2>
                    <span className="text-[11px] text-muted-foreground">Revenue & activity by region</span>
                </div>

                {/* Stacked revenue bars */}
                <div className="space-y-2">
                    {territory_breakdown.map((t) => {
                        const meta = TERRITORY_META[t.territory] || TERRITORY_META.other;
                        const widthPct = (t.monthly_revenue / maxRev) * 100;
                        return (
                            <div key={t.territory} className="flex items-center gap-3" data-testid={`territory-bar-${t.territory}`}>
                                <div className="w-20 text-xs font-bold flex items-center gap-1.5 shrink-0">
                                    <span className="w-2 h-2 rounded-full" style={{ background: meta.color }} />
                                    {meta.label}
                                </div>
                                <div className="flex-1 h-8 bg-secondary/40 rounded relative overflow-hidden">
                                    <div className="h-full rounded transition-all duration-500" style={{ width: `${widthPct}%`, background: meta.color, opacity: 0.85 }} />
                                    <div className="absolute inset-0 px-3 flex items-center text-[11px] font-bold text-white drop-shadow-sm">
                                        {fmtINR(t.monthly_revenue)}
                                    </div>
                                </div>
                                <div className="w-20 text-right text-[11px] text-muted-foreground tabular-nums">
                                    {t.cases_per_month} cases
                                </div>
                                <div className="w-20 text-right text-[11px] text-muted-foreground tabular-nums">
                                    {t.active_doctors} active
                                </div>
                                <div className="w-16 text-right text-[11px] font-bold text-emerald-600 tabular-nums">
                                    {t.conversion_pct}%
                                </div>
                            </div>
                        );
                    })}
                </div>

                {/* Numeric grid */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mt-5">
                    {territory_breakdown.map((t) => {
                        const meta = TERRITORY_META[t.territory] || TERRITORY_META.other;
                        return (
                            <div key={t.territory} className="bg-secondary/30 rounded-xl p-3" data-testid={`territory-card-${t.territory}`}>
                                <div className="flex items-center justify-between gap-2 mb-2">
                                    <div className="text-[10px] uppercase tracking-wider font-bold flex items-center gap-1.5">
                                        <span className="w-2 h-2 rounded-full" style={{ background: meta.color }} />
                                        {meta.label}
                                    </div>
                                    <div className="text-[10px] font-bold text-emerald-600 tabular-nums">{t.conversion_pct}%</div>
                                </div>
                                <div className="font-display text-xl font-bold tabular-nums">{fmtINR(t.monthly_revenue)}</div>
                                <div className="text-[10px] text-muted-foreground">monthly revenue</div>
                                <div className="grid grid-cols-3 gap-1.5 mt-2 pt-2 border-t border-border/40">
                                    <div>
                                        <div className="text-xs font-bold tabular-nums">{t.total_prospects}</div>
                                        <div className="text-[9px] uppercase tracking-wider text-muted-foreground">Total</div>
                                    </div>
                                    <div>
                                        <div className="text-xs font-bold tabular-nums">{t.active_doctors}</div>
                                        <div className="text-[9px] uppercase tracking-wider text-muted-foreground">Active</div>
                                    </div>
                                    <div>
                                        <div className="text-xs font-bold tabular-nums">{t.cases_per_month}</div>
                                        <div className="text-[9px] uppercase tracking-wider text-muted-foreground">Cases/mo</div>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </section>
        </div>
    );
}


function LeaderboardRow({ rank, row, expanded, onToggle }) {
    const rankTint = rank === 1 ? "bg-amber-100 text-amber-700"
        : rank === 2 ? "bg-slate-100 text-slate-700"
        : rank === 3 ? "bg-orange-100 text-orange-700"
        : "bg-secondary text-muted-foreground";

    return (
        <div className="border border-border rounded-lg overflow-hidden">
            <button
                type="button"
                onClick={onToggle}
                className="w-full flex items-center gap-3 p-3 hover:bg-secondary/30 text-left"
                data-testid={`leaderboard-row-${rank}`}
            >
                <div className={`w-7 h-7 rounded-full grid place-items-center text-[11px] font-bold ${rankTint} shrink-0`}>
                    #{rank}
                </div>
                <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold truncate">{row.rep_name}</div>
                    <div className="text-[10px] text-muted-foreground uppercase tracking-wider">
                        {row.territory || "—"} · {row.role === "sales_manager" ? "Sales Manager" : "Sales & KAM"}
                    </div>
                </div>
                <Metric label="Prospects" value={row.total_prospects} />
                <Metric label="Active" value={row.active_doctors} accent="primary" />
                <Metric label="Cases/mo" value={row.cases_per_month} hideOnSm />
                <Metric label="Revenue" value={fmtINR(row.monthly_revenue)} hideOnSm />
                <Metric label="Conv %" value={`${row.conversion_pct}%`} accent="emerald" />
                <div className="ml-2 shrink-0">
                    {expanded ? <CaretDown size={14} className="text-muted-foreground" /> : <CaretRight size={14} className="text-muted-foreground" />}
                </div>
            </button>
            {expanded && (
                <div className="px-4 pb-3 pt-1 bg-secondary/20" data-testid={`leaderboard-row-${rank}-detail`}>
                    <div className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground mb-2">Pipeline distribution</div>
                    <div className="grid grid-cols-7 gap-1.5">
                        {STAGE_ORDER.map((s) => {
                            const meta = STAGE_META[s];
                            const cnt = row.by_stage?.[s] || 0;
                            return (
                                <div key={s} className="text-center" data-testid={`leaderboard-row-${rank}-stage-${s}`}>
                                    <div className="font-display text-base font-bold tabular-nums">{cnt}</div>
                                    <div className="flex items-center justify-center gap-1 mt-0.5">
                                        <span className="w-1.5 h-1.5 rounded-full" style={{ background: meta.color }} />
                                        <span className="text-[9px] uppercase tracking-wider text-muted-foreground">{meta.label}</span>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}
        </div>
    );
}


function Metric({ label, value, accent, hideOnSm }) {
    const cls = accent === "emerald" ? "text-emerald-600" : accent === "primary" ? "text-primary" : "";
    return (
        <div className={`text-right shrink-0 ${hideOnSm ? "hidden md:block" : ""}`}>
            <div className={`text-sm font-bold tabular-nums ${cls}`}>{value}</div>
            <div className="text-[9px] uppercase tracking-wider text-muted-foreground">{label}</div>
        </div>
    );
}
